import { v4 as uuidv4 } from 'uuid';

import HaspObject from './HaspObject';
import CustomRect from '../draw/CustomRect';
import { lvglBarIndicatorBounds, lvglBarMaximumArea, normalizeRangeValues, quantizeBarValue } from './barGeometry.js';
import { OPENHASP_CONTROL_TYPE_OPTIONS } from './controlTypes.js';
import { attachImageSourcePreview, attachTrackImageSizeLock } from './imageSource.js';
import { onPaddingStyle, resolvePadding } from './properties/v2/padding.js';

// min	        int16	0	minimum value of the indicator
// max	        int16	100	maximum value of the indicator
// val	        int16	0	current value of the indicator
// start_value	int16	0	optional minimal allowed value of the indicator

export default class HaspBar extends HaspObject {
    isHorizontal = true;

    hasp_type_options = OPENHASP_CONTROL_TYPE_OPTIONS

    // hassTemplate = '/media/templates/bar.ftl';

    constructor(config) {
        super(config);

        this._trackImageSizeLocked = false;
        this._trackImageNativeSize = null;
        this._trackImagePreviousStretchModeSet = false;
        this._trackImagePreviousStretchMode = undefined;

        const indicator = this.style('indicator');
        this.indicator = new CustomRect({
            id: uuidv4(),
            x: 0,
            y: 0,
            width: 0,
            height: this.height(),
            fill: indicator.bg_color,
            draggable: false,
            name: 'value',
            obj: 'indicator',
            selectable: false,
            cornerRadius: indicator.radius,
            stroke: indicator.border_color,
            borderWidth: indicator.border_width,
            listening: false,
        });

        this.on('transform', function () {
            if (this._trackImageSizeLocked && !this._applyingTrackImageSize && this._trackImageNativeSize) {
                this.applyTrackImageSize(this._trackImageNativeSize);
                return;
            }
            this.updateSize();
            this.updateValue();
        });

        this.on('transformend', function () {
            if (this._trackImageSizeLocked && !this._applyingTrackImageSize && this._trackImageNativeSize) {
                this.applyTrackImageSize(this._trackImageNativeSize);
                return;
            }
            this.updateSize();
            this.updateValue();
        });

        this.on('data_val', (e) => {
            this.updateValue();
        });
        this.on('data_min', (e) => {
            this.updateValue();
        });
        this.on('data_max', (e) => {
            this.updateValue();
        });
        this.on('data_start_value', () => {
            this.updateValue();
        });
        this.on('data_step', () => {
            this.updateValue();
        });
        if (config.obj === 'bar') {
            this.on('type', () => this.updateValue());
        }

        for (const prop of ['bg_color', 'bg_grad_color', 'bg_grad_dir', 'bg_main_stop', 'bg_grad_stop']) {
            this.onStyle('indicator', prop, () => {
                this.applyBackgroundStyle(this.indicator, 'indicator');
            });
        }

        this.onStyle('indicator', 'border_width', () => {
            const width = Number(this.style('indicator').border_width);
            this.indicator.setStrokeWidth?.(width);
            if (!this.indicator.setStrokeWidth) this.indicator.strokeWidth(width);
        })
        this.onStyle('indicator', 'border_color', () => {
            this.indicator.stroke(this.style('indicator').border_color);
        })

        // main's radius is already fully handled by HaspObject's generic
        // .on('radius', ...) listener, which applies directly to this.rect
        // (the bar's own visible track background, unlike other widgets
        // where this.rect is an invisible hit-box) — no Bar-specific listener
        // needed there. indicator's radius has no such generic handler
        // (this.indicator is Bar-specific), so it still needs its own.
        this.onStyle('indicator', 'radius', () => {
            this.indicator.cornerRadius(this.style('indicator').radius);
        });

        // BG padding is the only Bar/Slider padding that changes the LVGL
        // indicator track. INDIC padding is intentionally not a schema
        // property: lv_bar.c never reads it for geometry or drawing.
        onPaddingStyle(this, 'main', () => {
            this.updateSize();
            this.updateValue();
        });

        this.add(this.indicator);

        // BG and INDIC image sources are shared by Bar and Slider. BG uses the
        // object bounds; INDIC uses the maximum BG-padded track as its native
        // placement area and the live indicator as its clip.
        this.mainImagePreview = attachImageSourcePreview(this, {
            part: 'main',
            getBounds: () => {
                const track = this.trackContentBounds();
                return { imageBounds: track, clipBounds: track, clipRadius: this.styleProp('main', 'radius') };
            },
        });
        this.add(this.mainImagePreview.imageNode);
        this.mainImagePreview.imageNode.zIndex(this.indicator.zIndex());

        this.indicatorImagePreview = attachImageSourcePreview(this, {
            part: 'indicator',
            getBounds: () => ({
                // lv_bar.c draws the INDIC pattern in the maximum BG-padded
                // area, then masks it with the current indicator area. Keep
                // those two rectangles separate so val changes reveal more
                // or less of one fixed native-size image.
                imageBounds: this.trackMaximumBounds(),
                clipBounds: {
                    x: this.indicator.x(),
                    y: this.indicator.y(),
                    width: this.indicator.width(),
                    height: this.indicator.height(),
                },
                clipRadius: this.styleProp('indicator', 'radius'),
            }),
        });
        this.add(this.indicatorImagePreview.imageNode);
        this.indicatorImagePreview.imageNode.zIndex(this.indicator.zIndex() + 1);

        this.trackImageSizeBinding = attachTrackImageSizeLock(this);

        this.applyBackgroundStyle(this.indicator, 'indicator');
        this.draggable(true);
        this.updateSize();
        this.updateValue();
    }

    setNewSize(size) {
        if (this._trackImageSizeLocked && !this._applyingTrackImageSize && this._trackImageNativeSize) {
            this.applyTrackImageSize(this._trackImageNativeSize);
            return;
        }
        super.setNewSize(size);
        this.updateSize();
        this.updateValue();
    }

    applyTrackImageSize(size) {
        const width = Math.round(Number(size?.width));
        const height = Math.round(Number(size?.height));
        if (!(width > 0 && height > 0)) return;

        if (!this._trackImageSizeLocked) {
            this._trackImagePreviousStretchModeSet = Object.prototype.hasOwnProperty.call(this, 'stretch_mode');
            this._trackImagePreviousStretchMode = this.stretch_mode;
            this._trackImageSizeLocked = true;
        }
        this._trackImageNativeSize = { width, height };
        this.stretch_mode = 'none';

        if (this.width() !== width || this.height() !== height) {
            this._applyingTrackImageSize = true;
            try {
                // Dynamic dispatch is intentional: HaspSlider's override also
                // synchronizes its knob after the shared track resize.
                this.setNewSize({ width, height });
            } finally {
                this._applyingTrackImageSize = false;
            }
        }

        this.getLayer()?.updateTransformer?.();
        this.getLayer()?.updateObjectMenu?.(this);
        this.getLayer()?.requestRender?.();
        this.getLayer()?.batchDraw?.();
        this.dispatchEvent(new Event('track-image-size'));
    }

    clearTrackImageSizeLock() {
        if (!this._trackImageSizeLocked) return;

        this._trackImageSizeLocked = false;
        this._trackImageNativeSize = null;
        if (this._trackImagePreviousStretchModeSet) this.stretch_mode = this._trackImagePreviousStretchMode;
        else delete this.stretch_mode;
        this._trackImagePreviousStretchModeSet = false;
        this._trackImagePreviousStretchMode = undefined;

        this.getLayer()?.updateTransformer?.();
        this.getLayer()?.updateObjectMenu?.(this);
        this.getLayer()?.requestRender?.();
        this.getLayer()?.batchDraw?.();
        this.dispatchEvent(new Event('track-image-size'));
    }

    normalizeDataValues() {
        const normalized = normalizeRangeValues({
            min: this.min,
            max: this.max,
            value: this.val,
            startValue: this.start_value,
            step: this.step,
        });
        if (!normalized) return;
        this.val = normalized.value;
        this.start_value = normalized.startValue;
    }

    /**
     * Single local coordinate space for both Bar image parts. The rect is the
     * track/content geometry after any Konva transform has been committed;
     * indicator bounds never participate in image fitting.
     */
    trackContentBounds() {
        const width = Math.max(0, Number(this.rect?.width?.() ?? this.width?.() ?? 0));
        const height = Math.max(0, Number(this.rect?.height?.() ?? this.height?.() ?? 0));
        return { x: 0, y: 0, width, height };
    }

    trackPadding() {
        return resolvePadding(this.style('main'));
    }

    trackMaximumBounds() {
        const track = this.trackContentBounds();
        const area = lvglBarMaximumArea({
            width: track.width,
            height: track.height,
            padding: this.trackPadding(),
            horizontal: this.isHorizontal,
        });
        return {
            x: area.x1,
            y: area.y1,
            width: Math.max(0, area.x2 - area.x1 + 1),
            height: Math.max(0, area.y2 - area.y1 + 1),
        };
    }

    clipToTrack(bounds) {
        const track = this.trackContentBounds();
        const source = bounds ?? track;
        const left = Math.max(track.x, Number(source.x ?? 0));
        const top = Math.max(track.y, Number(source.y ?? 0));
        const right = Math.min(track.x + track.width, Number(source.x ?? 0) + Math.max(0, Number(source.width ?? 0)));
        const bottom = Math.min(track.y + track.height, Number(source.y ?? 0) + Math.max(0, Number(source.height ?? 0)));
        return {
            x: left,
            y: top,
            width: Math.max(0, right - left),
            height: Math.max(0, bottom - top),
        };
    }

    quantizeValue(value) {
        return quantizeBarValue(value, this.min, this.max, this.step);
    }

    setRangeValues(startValue, value) {
        const normalized = normalizeRangeValues({
            min: this.min,
            max: this.max,
            value,
            startValue,
            step: this.step,
        });
        if (!normalized) return false;
        this.start_value = normalized.startValue;
        this.val = normalized.value;
        if (typeof this.syncKnobFromValue === 'function') this.syncKnobFromValue();
        else if (typeof this.updateValue === 'function') this.updateValue();
        return true;
    }

    adjust(type, value) {
        const liveValueInput = ['data_val', 'data_start_value', 'data_step'].includes(type);
        const previousDefer = this._deferValueNormalization;
        if (liveValueInput) this._deferValueNormalization = true;
        if (['data_min', 'data_max'].includes(type)) {
            this.normalizeDataValues();
        }
        try {
            super.adjust(type, value);
        } finally {
            this._deferValueNormalization = previousDefer;
        }
    }

    /** Commit a number-input edit after input/change/Enter, not per keystroke. */
    commitDataProperty(property) {
        if (!['val', 'start_value', 'step'].includes(property)) return;
        this._deferValueNormalization = false;
        this.normalizeDataValues();
        if (typeof this.syncKnobFromValue === 'function') this.syncKnobFromValue();
        else this.updateValue();
        this.adjust(`data_${property}`, this[property]);
    }

    updateSize() {
        if (this.height() < this.width()) {
            this.isHorizontal = true;
            this.indicator.height(this.rect.height());
        } else {
            this.isHorizontal = false;
            this.indicator.width(this.rect.width());
        }
        // Defensive re-application after a size change — cornerRadius isn't
        // implied by size() alone.
        this.rect.cornerRadius(this.styleProp('main', 'radius'));
        this.indicator.cornerRadius(this.style('indicator').radius);
        this.size(this.rect.size());
        // Refit from the committed full track rect after orientation and size
        // have been updated. The indicator image is refreshed later by
        // setBarIndicator(), once its clip has been calculated.
        this.mainImagePreview?.updateBounds();
    }

    updateValue() {
        if (!this._deferValueNormalization) this.normalizeDataValues();
        const geometry = lvglBarIndicatorBounds({
            type: this.obj === 'bar' ? this.type : 0,
            min: this.min,
            max: this.max,
            value: this.val,
            startValue: this.start_value,
            width: this.width(),
            height: this.height(),
            horizontal: this.isHorizontal,
            padding: this.trackPadding(),
        });
        this.setBarIndicatorBounds(geometry);
        return this.isHorizontal ? geometry.x : geometry.y;
    }

    setBarIndicatorBounds(bounds) {
        this.indicator.x(bounds.x);
        this.indicator.y(bounds.y);
        this.indicator.width(bounds.width);
        this.indicator.height(bounds.height);
        this.indicatorImagePreview?.updateBounds();
    }

    setBarIndicator(start, size) {
        if (this.isHorizontal) {
            this.indicator.x(start);
            this.indicator.y(0);
            this.indicator.width(size);
            this.indicator.height(this.height());
        } else {
            this.indicator.x(0);
            this.indicator.y(start);
            this.indicator.width(this.width());
            this.indicator.height(size);
        }
        this.indicatorImagePreview?.updateBounds();
    }

    setBarVal(barPos) {
        if (this.isHorizontal) {
            this.indicator.y(0);
            this.indicator.width(barPos);
        } else {
            this.indicator.y(barPos);
            this.indicator.height(this.height() - barPos);
        }
    }
}
